/**
 * Landing v3 — the feature cards' media slots hold the REAL product
 * components (the study card, the quiz, the mind map, the notes editor, the
 * occlusion editor, the exam planner, the transcript) fed with sample study
 * content, so the landing shows what the apps ship and changes when they do.
 *
 * Two rules make that affordable on a public page:
 *
 * 1. EVERY media is its own `React.lazy` chunk. The landing route is imported
 *    eagerly in `App.tsx` (to avoid a Suspense flash on `/`), so a static
 *    import here would put TipTap, xyflow and KaTeX in the initial bundle for
 *    every signed-out visitor. Chunks are fetched ahead of need by deck
 *    position (`preloadFeatureMedia`), never on the critical path.
 * 2. Mounting follows the DECK, not the viewport — on the sticky stage every
 *    card is "in view". The front card is `live` (interactive, animating); the
 *    card under it is `warm` (mounted, inert, paying its layout cost while the
 *    reader looks at the front card, never during a throw); everything else is
 *    unmounted. At most one live and one warm component exist at a time, on
 *    top of the page's own Ka (Rive), dot map and marquee.
 */
import { type ComponentType, lazy, type ReactNode, Suspense, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import i18n from '@/i18n';
import { cn } from '@/lib/utils';

import { loadLandingSamples } from './sample-content';

/** Where a card sits in the deck, from the media's point of view. */
export type MediaPhase =
  /** Deep in the pile (or before the deck rose): nothing mounted. */
  | 'idle'
  /** Next up: mounted and laid out, but inert and not animating. */
  | 'warm'
  /** Next up and already half uncovered (the card above is half thrown, or
   *  on the phone half of it is on screen): inert, but EVERY media is
   *  mounted, so a build-in canvas is drawing as it comes into sight. */
  | 'revealing'
  /** The front card, at rest: interactive, animations running. */
  | 'live'
  /** Mid-throw: still mounted so it doesn't blink out, no longer interactive. */
  | 'leaving'
  /** Thrown away: unmounted. */
  | 'gone';

export type MediaProps = {
  /** True only while the card is the front card at rest. */
  live: boolean;
  /**
   * The phone layout (user 2026-09-28): the component plays its intro and
   * then sits still — no canvas that takes the finger (a map that pans, an
   * editor that draws or types), because on a touch screen that finger is
   * scrolling the page. Taps that don't trap a scroll (flip, rate, answer,
   * seek) still work.
   */
  still?: boolean;
  /**
   * The phone's pile (user 2026-09-29, Figma 116:1267): the demo shows only
   * its essential piece — the flashcard alone, the transcriber's player, the
   * note's text — laid out at its stage size; the card scales it to fit.
   * Separate from `still`: an iPad's touch screen gets the desktop's cards.
   */
  compact?: boolean;
};

type Loader = () => Promise<{ default: ComponentType<MediaProps> }>;

/** Feature id (see `landing-features.ts`) → its lazily loaded media. A card
 *  without an entry keeps the empty slot. */
const LOADERS: Record<string, Loader> = {
  flashcards: () => import('./flashcards-media'),
  mindMaps: () => import('./mindmap-media'),
  transcriber: () => import('./transcript-media'),
  summaries: () => import('./notes-media'),
  imageOcclusion: () => import('./occlusion-media'),
  examPlanner: () => import('./planner-media'),
  spacedRepetition: () => import('./sr-media'),
  quizzes: () => import('./quiz-media'),
  oralExam: () => import('./oral-exam-media'),
  caseStudies: () => import('./case-study-media'),
};

/** Canvases whose build-in animation is the point (the mind map streams its
 *  nodes in, the occlusion editor sweeps its scan) mount once the card is
 *  half uncovered (`revealing`), so the reader sees it play — not before,
 *  where it would play unseen, and not as late as the front, where the
 *  uncovered half sat as an empty dark well (user 2026-10-09). Everything
 *  else mounts a card early. */
const MOUNT_AT_LIVE = new Set(['mindMaps', 'imageOcclusion']);

const LAZY: Record<string, ComponentType<MediaProps>> = Object.fromEntries(
  Object.entries(LOADERS).map(([id, load]) => [id, lazy(load)]),
);

/** A media's chunk failed (offline, deploy skew): React.lazy keeps a rejected
 *  import forever, so the next mount gets a fresh lazy that fetches again. */
function forgetMedia(featureId: string): void {
  const load = LOADERS[featureId];
  if (load) LAZY[featureId] = lazy(load);
}

const requested = new Set<string>();
/** Media whose chunk has arrived, by feature id: rendered directly, so a
 *  preloaded media never suspends (React.lazy still suspends once per
 *  component even when its import() has settled — a ~300ms placeholder pulse
 *  and a second commit, mid-throw). */
const READY: Record<string, ComponentType<MediaProps>> = {};

/** Fetch a media's chunk ahead of time, and keep the component once it lands.
 *  The sample content in the page's language comes along (fetched once). */
export function preloadFeatureMedia(featureId: string): void {
  if (requested.has(featureId)) return;
  const load = LOADERS[featureId];
  if (!load) return;
  requested.add(featureId);
  // A failure is retried by the media's own render.
  loadLandingSamples(i18n.language).catch(() => {});
  // A failed prefetch (offline, deploy skew) is retried by the real render.
  void load().then(
    (m) => {
      READY[featureId] = m.default;
    },
    () => {
      requested.delete(featureId);
      forgetMedia(featureId);
    },
  );
}

/** Whether a feature has a media at all (every card has one now): without,
 *  the phone's pile would show the feature's chip in the slot instead. */
export const hasFeatureMedia = (featureId: string) => featureId in LOADERS;

export function FeatureMedia({
  featureId,
  phase,
  still = false,
  compact = false,
}: {
  featureId: string;
  phase: MediaPhase;
  /** See `MediaProps.still`. */
  still?: boolean;
  /** See `MediaProps.compact`. */
  compact?: boolean;
}) {
  const mounted =
    phase === 'live' ||
    phase === 'leaving' ||
    phase === 'revealing' ||
    (phase === 'warm' && !MOUNT_AT_LIVE.has(featureId));
  if (!(featureId in LOADERS) || !mounted) return <MediaPlaceholder />;
  return (
    // A media that throws degrades to the empty slot — never to a blank page.
    // It tries again once when its card reaches the front (a failed chunk
    // fetched afresh), not in a loop: a second failure keeps the slot empty
    // until the card comes round again.
    <ErrorBoundary
      key={featureId}
      // Flips once, warm → live: arriving on top retries; the throw doesn't.
      resetKeys={[phase === 'live' || phase === 'leaving']}
      onError={() => forgetMedia(featureId)}
      fallback={<MediaPlaceholder />}>
      <Suspense fallback={<MediaPlaceholder loading />}>
        <PinnedMedia
          key={featureId}
          featureId={featureId}
          live={phase === 'live'}
          still={still}
          compact={compact}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

/**
 * The media, its component picked ONCE per mount: the lazy one when the card
 * mounted before its chunk arrived, the loaded one after. Switching to the
 * loaded one when it lands mid-life would be a different component to React
 * — a remount that wipes what the visitor did (a quiz half answered, an exam
 * under way, a card edited).
 */
function PinnedMedia({ featureId, ...props }: MediaProps & { featureId: string }) {
  const [Media] = useState(() => READY[featureId] ?? LAZY[featureId]);
  return <Media {...props} />;
}

/** The slot as it was before the media existed: the card's grey well, pulsing
 *  softly while a chunk is on its way. */
function MediaPlaceholder({ loading = false }: { loading?: boolean }) {
  return (
    <div
      aria-hidden
      className={cn('h-full w-full', loading && 'animate-pulse bg-secondary-100/60')}
    />
  );
}

/**
 * The frame every media sits in: a centred component on the slot's dotted
 * well (Figma 60:21855). `plain` covers the dots for canvases that draw their
 * own background.
 */
export function MediaFrame({
  children,
  className,
  plain = false,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  plain?: boolean;
  padded?: boolean;
}) {
  return (
    <div
      // The phone's pile halves the padding (index.css, `.lv3-pile-well`).
      data-lv3-media-frame={padded ? 'padded' : undefined}
      className={cn(
        'relative flex h-full w-full items-center justify-center overflow-clip',
        // The slot itself carries the dot lattice (landing-chrome); `plain`
        // covers it for canvases that draw their own background.
        plain && 'bg-background',
        padded && 'p-6',
        className,
      )}>
      {children}
    </div>
  );
}

/** The white product surface most media float on (Figma 60:21855's card). */
export const MEDIA_SURFACE_CLASS =
  'rounded-[32px] border border-secondary-200 bg-secondary-50 shadow-card-row';
